// client/src/components/vendas/VendaRow.js
import React from 'react';
import { MoreVertical, Calendar, User, ShoppingCart, CreditCard } from 'lucide-react';
import { useTenantI18n } from '../../hooks/useAccounts';
import { formatCurrency as formatCurrencyUtil } from '../../utils/formatters';

const VendaRow = ({ venda, onOpenActions }) => {
    // ✅ ACHADO: moeda fixa em BRL local ao componente, sem ler a
    // configuração de idioma/moeda da conta (configuracoesGerais.i18n).
    const { currency, locale } = useTenantI18n();

    const formatDate = (dateString) => {
        if (!dateString) return 'Data não informada';
        
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return 'Data inválida';
            }
            return date.toLocaleDateString('pt-BR');
        } catch (error) {
            console.error('Erro ao formatar data:', error);
            return 'Data inválida';
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'Data não informada';
        
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return 'Data inválida';
            }
            return date.toLocaleString('pt-BR');
        } catch (error) {
            console.error('Erro ao formatar data:', error);
            return 'Data inválida';
        }
    };

    const formatCurrency = (value) => {
        if (value === undefined || value === null) return formatCurrencyUtil(0, currency, locale);
        return formatCurrencyUtil(value, currency, locale);
    };

    const getStatusBadge = (status) => {
        const baseClasses = "px-3 py-1 text-xs font-semibold rounded-full border";
        switch (status) {
            case 'Aberta':
                return `${baseClasses} bg-blue-50 text-blue-700 border-blue-200`;
            case 'Finalizada':
                return `${baseClasses} bg-green-50 text-green-700 border-green-200`;
            case 'Cancelada':
                return `${baseClasses} bg-red-50 text-red-700 border-red-200`;
            default:
                return `${baseClasses} bg-gray-50 text-gray-700 border-gray-200`;
        }
    };

    const getPagamentoBadge = (status) => {
        const baseClasses = "px-3 py-1 text-xs font-semibold rounded-full border";
        switch (status) {
            case 'Pago':
                return `${baseClasses} bg-green-50 text-green-700 border-green-200`;
            case 'Parcialmente Pago':
                return `${baseClasses} bg-yellow-50 text-yellow-700 border-yellow-200`;
            case 'Pendente':
                return `${baseClasses} bg-orange-50 text-orange-700 border-orange-200`;
            case 'Inadimplente':
                return `${baseClasses} bg-red-50 text-red-700 border-red-200`;
            default:
                return `${baseClasses} bg-gray-50 text-gray-700 border-gray-200`;
        }
    };

    // Obter nome do cliente de forma segura
    const getClienteNome = () => {
        if (!venda.cliente) return 'Cliente não informado';
        
        if (typeof venda.cliente === 'string') {
            return 'Carregando...';
        }
        
        return venda.cliente.nome || 'Cliente não informado';
    };

    // Verificar se cliente foi populado
    const isClientePopulated = venda.cliente && typeof venda.cliente === 'object';

    // 🔥 NOVO: Obter método de pagamento principal
    const getMetodoPagamento = () => {
        if (!venda.pagamentos || venda.pagamentos.length === 0) return 'N/A';
        
        const pagamentoPrincipal = venda.pagamentos[0];
        if (pagamentoPrincipal.metodo && typeof pagamentoPrincipal.metodo === 'object') {
            return pagamentoPrincipal.metodo.nome || 'N/A';
        }
        
        return 'N/A';
    };

    return (
        <tr className="table-row hover:bg-gray-50 transition-colors duration-150">
            <td className="table-cell">
                <div className="flex items-center gap-2">
                    <User size={16} className="text-gray-400 flex-shrink-0" />
                    <span className={`truncate max-w-[150px] ${!isClientePopulated ? 'text-gray-500 italic' : ''}`}>
                        {getClienteNome()}
                    </span>
                </div>
            </td>
            <td className="table-cell">
                <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                        <Calendar size={14} className="text-gray-400 flex-shrink-0" />
                        <span className="text-sm font-medium">
                            {venda.dataFormatada || formatDate(venda.dataVenda)}
                        </span>
                    </div>
                    <span className="text-xs text-gray-500">
                        {venda.dataHoraFormatada || formatDateTime(venda.dataVenda)}
                    </span>
                </div>
            </td>
            <td className="table-cell">
                <div className="flex items-center gap-2">
                    <ShoppingCart size={16} className="text-gray-400 flex-shrink-0" />
                    <span className="text-sm font-medium">
                        {venda.itens?.length || 0} item(s)
                    </span>
                </div>
            </td>
            <td className="table-cell">
                <div className="font-semibold text-green-700">
                    {formatCurrency(venda.total)}
                </div>
                {venda.descontoGeral > 0 && (
                    <div className="text-xs text-red-600">
                        -{formatCurrency(venda.descontoGeral)}
                    </div>
                )}
            </td>
            <td className="table-cell">
                <span className={getStatusBadge(venda.statusVenda)}>
                    {venda.statusVenda || 'N/A'}
                </span>
            </td>
            <td className="table-cell">
                <div className="flex flex-col gap-1">
                    <span className={getPagamentoBadge(venda.statusPagamento)}>
                        {venda.statusPagamento || 'N/A'}
                    </span>
                    {venda.pagamentos && venda.pagamentos.length > 0 && (
                        <div className="flex items-center gap-1 text-xs text-gray-600">
                            <CreditCard size={12} />
                            <span>{getMetodoPagamento()}</span>
                        </div>
                    )}
                </div>
            </td>
            <td className="text-right">
                <div className="flex justify-end">
                    <button
                        onClick={() => onOpenActions(venda)}
                        className="action-icon-button text-gray-600"
                        title="Ações"
                    >
                        <MoreVertical size={18} />
                    </button>
                </div>
            </td>
        </tr>
    );
};

export default VendaRow;